import { useEffect, useMemo, useState } from "react";
import { BriefcaseBusiness, CheckCircle2, Clock3, Eye, Search } from "lucide-react";
import api from "Common/api";

type HistoryItem = {
  id: number;
  homeownerName: string;
  homeownerEmail: string;
  contractorName: string;
  contractorEmail: string;
  contractorCompany?: string;
  contractorLocation?: string;
  jobTitle?: string;
  status: string;
  createdAt: string;
  updatedAt?: string;
};

const statusLabels: Record<string, string> = {
  suggested: "Not viewed",
  viewed: "Viewed",
  contacted: "Contacted",
  invited: "Invited",
  requested: "Hire requested",
  hired: "Hired",
};

const statusClasses: Record<string, string> = {
  suggested: "contractor-history-status",
  viewed: "contractor-history-status contractor-history-status--viewed",
  contacted: "contractor-history-status contractor-history-status--contacted",
  invited: "contractor-history-status contractor-history-status--invited",
  requested: "contractor-history-status contractor-history-status--requested",
  hired: "contractor-history-status contractor-history-status--hired",
};

export function RecommendationHistory() {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [stats, setStats] = useState({ total: 0, notViewed: 0, viewed: 0, requested: 0, hired: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    setLoading(true);
    api.get("/agent/suggest-contractors/history")
      .then((response: any) => {
        setItems(Array.isArray(response?.data) ? response.data : []);
        setStats(response?.stats || { total: 0, notViewed: 0, viewed: 0, requested: 0, hired: 0 });
      })
      .catch((requestError: any) => {
        setError(requestError?.response?.data?.message || "Unable to load recommendation history.");
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesStatus = status === "all" || item.status === status;
      const matchesSearch = !query || [
        item.homeownerName,
        item.homeownerEmail,
        item.contractorName,
        item.contractorEmail,
        item.contractorCompany,
        item.jobTitle,
      ].filter(Boolean).join(" ").toLowerCase().includes(query);
      return matchesStatus && matchesSearch;
    });
  }, [items, search, status]);

  if (loading) {
    return <div className="flex min-h-[320px] items-center justify-center text-sm text-gray-500" role="status">Loading recommendation history...</div>;
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="mb-1 text-xl font-semibold text-gray-900">Contractor Recommendation History</h2>
        <p className="mb-0 text-sm text-gray-500">Track which homeowner received, viewed, requested, or hired each contractor.</p>
      </div>

      {error ? <div className="contractor-history-error rounded-lg border p-3 text-sm" role="alert">{error}</div> : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {[
          { label: "Sent", value: stats.total, icon: BriefcaseBusiness },
          { label: "Not viewed", value: stats.notViewed, icon: Clock3 },
          { label: "Viewed", value: stats.viewed, icon: Eye },
          { label: "Hire requested", value: stats.requested, icon: CheckCircle2 },
          { label: "Hired", value: stats.hired, icon: CheckCircle2 },
        ].map((card) => (
          <div key={card.label} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between text-sm text-gray-500"><span>{card.label}</span><card.icon className="h-4 w-4" /></div>
            <div className="text-2xl font-semibold text-gray-900">{card.value}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-100 p-4 md:flex-row">
          <div className="app-search-field flex-1">
            <Search className="app-field-icon" aria-hidden="true" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search homeowner or contractor..." className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-gray-400" />
          </div>
          <select value={status} onChange={(event) => setStatus(event.target.value)} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none">
            <option value="all">All statuses</option>
            {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr><th className="px-4 py-3">Homeowner</th><th className="px-4 py-3">Contractor</th><th className="px-4 py-3">Job / Context</th><th className="px-4 py-3">Sent</th><th className="px-4 py-3">Last activity</th><th className="px-4 py-3">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3"><div className="font-medium text-gray-900">{item.homeownerName}</div><div className="text-xs text-gray-500">{item.homeownerEmail}</div></td>
                  <td className="px-4 py-3"><div className="font-medium text-gray-900">{item.contractorCompany || item.contractorName}</div><div className="text-xs text-gray-500">{item.contractorName}{item.contractorLocation ? ` · ${item.contractorLocation}` : ""}</div></td>
                  <td className="px-4 py-3 text-gray-600">{item.jobTitle || "General recommendation"}</td>
                  <td className="px-4 py-3 text-gray-600">{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{item.updatedAt ? new Date(item.updatedAt).toLocaleString() : "No activity yet"}</td>
                  <td className="px-4 py-3"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClasses[item.status] || statusClasses.suggested}`}>{statusLabels[item.status] || item.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!filteredItems.length ? <div className="p-10 text-center text-sm text-gray-500">No recommendation history matches these filters.</div> : null}
      </div>
    </div>
  );
}
